import { useState } from "react";
import { Calendar } from "lucide-react";

export const PERIOD_OPTIONS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "last_quarter", label: "Last Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "custom", label: "Custom Range" },
];

// Not part of PERIOD_OPTIONS itself (Finance/payout pages never want an
// open-ended range) - pages like the appointments list that default to
// "everything booked ahead" opt into it explicitly via PeriodPicker's
// `options` prop, e.g. [ALL_UPCOMING_OPTION, ...PERIOD_OPTIONS].
export const ALL_UPCOMING_OPTION = { key: "all_upcoming", label: "All Upcoming" };

// Same idea, for pages (Clinical Skills tab) whose historical default is
// "everything ever recorded," not bounded to a calendar period.
export const ALL_TIME_OPTION = { key: "all_time", label: "All Time" };

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0); // rolls year/month correctly at either edge

// Resolves every preset except "custom" to an inclusive {start, end} ISO
// date pair - "custom" is resolved by the caller from its own two date
// inputs instead, since there's nothing to compute.
export function resolvePresetRange(period) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (period) {
    case "this_month":
      return { start: toISO(new Date(y, m, 1)), end: toISO(lastDayOfMonth(y, m)) };
    case "last_month":
      return { start: toISO(new Date(y, m - 1, 1)), end: toISO(lastDayOfMonth(y, m - 1)) };
    case "this_quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return { start: toISO(new Date(y, qStart, 1)), end: toISO(lastDayOfMonth(y, qStart + 2)) };
    }
    case "last_quarter": {
      const qStart = Math.floor(m / 3) * 3 - 3;
      return { start: toISO(new Date(y, qStart, 1)), end: toISO(lastDayOfMonth(y, qStart + 2)) };
    }
    case "this_year":
      return { start: toISO(new Date(y, 0, 1)), end: toISO(new Date(y, 11, 31)) };
    case "all_upcoming":
      // Open-ended on purpose - end: null means "no upper bound" to every
      // caller (the API param is simply omitted rather than sent empty).
      return { start: toISO(now), end: null };
    case "all_time":
      // Both ends open - every caller omits both date params entirely,
      // preserving whatever all-time/last-N default that endpoint already had.
      return { start: null, end: null };
    default:
      return null;
  }
}

export const formatPeriodLabel = (iso) => {
  if (!iso) return "";
  const [y, mo, d] = iso.split("-").map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// Shared period-filter state, used identically by the Finance tab and every
// payout drill-down/admin page so "This Month/Last Month/.../Custom Range"
// always behaves the same way everywhere it appears.
export function usePeriodFilter(initial = {}) {
  const [period, setPeriodState] = useState(initial.period || "this_month");
  const [customStart, setCustomStart] = useState(initial.customStart || "");
  const [customEnd, setCustomEnd] = useState(initial.customEnd || "");

  const setPeriod = (value) => {
    setPeriodState(value);
    // Seed the custom inputs with this month's range the first time someone
    // switches to Custom, rather than leaving both date fields empty with
    // nothing to fetch until they've filled in both by hand.
    if (value === "custom" && !customStart && !customEnd) {
      const thisMonth = resolvePresetRange("this_month");
      setCustomStart(thisMonth.start);
      setCustomEnd(thisMonth.end);
    }
  };

  const range = period === "custom"
    ? (customStart && customEnd ? { start: customStart, end: customEnd } : null)
    : resolvePresetRange(period);

  return { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, range };
}

const selectClass = "w-full sm:w-48 px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all cursor-pointer hover:bg-white/80 text-sm";
const dateInputClass = "px-3 py-2 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm";

// Presentational picker - pass in the state from usePeriodFilter() plus an
// optional resolvedLabel (e.g. the period actually echoed back by the API,
// which can only be known once data has loaded).
// `leading` (optional) renders extra filters - e.g. Finance's branch/doctor
// pickers - at the start of the same row, with the period controls pushed to
// the end, instead of stacking them as a separate row above.
export function PeriodPicker({ period, onPeriodChange, customStart, onCustomStartChange, customEnd, onCustomEndChange, resolvedLabel, options = PERIOD_OPTIONS, leading = null }) {
  const controls = (
    <>
      {resolvedLabel && (
        <span className="text-sm text-secondary-400">{resolvedLabel}</span>
      )}
      <div className="flex items-center gap-2">
        <Calendar size={16} className="text-secondary-400 shrink-0" />
        <select className={selectClass} value={period} onChange={(e) => onPeriodChange(e.target.value)}>
          {options.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </div>
      {period === "custom" && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customStart}
            max={customEnd || undefined}
            onChange={(e) => onCustomStartChange(e.target.value)}
            className={dateInputClass}
          />
          <span className="text-secondary-400 text-sm">to</span>
          <input
            type="date"
            value={customEnd}
            min={customStart || undefined}
            onChange={(e) => onCustomEndChange(e.target.value)}
            className={dateInputClass}
          />
        </div>
      )}
    </>
  );

  if (leading) {
    return (
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        {leading}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:ml-auto">{controls}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
      {controls}
    </div>
  );
}
